// Screenshot capture + ingest for the QA reporter.
//
// HARD LESSON ENCODED HERE: never render the DOM to a canvas in JS (html-to-image /
// domToCanvas). Serializing document.body blocks the main thread on heavy pages
// (big CRM grids) and a timeout can abandon but not cancel that work - the tab
// stays frozen. getDisplayMedia uses the browser compositor instead: real pixels,
// correct inner scroll, open popovers, and the heavy work is off the main thread.
export const MAX_SHOTS = 5;
const MAX_WIDTH = 1600;
const MAX_HEIGHT = 4000;
const JPEG_QUALITY = 0.82;
const FRAME_WAIT_CAP_MS = 4000;
const OVERALL_CAP_MS = 6000;
const LOADEDDATA_PAINT_GRACE_MS = 150;

export const nativeCaptureSupported = () =>
  !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function");

const drawScaled = (source, sourceWidth, sourceHeight) => {
  const scale = Math.min(1, MAX_WIDTH / sourceWidth, MAX_HEIGHT / sourceHeight);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
};

// rVFC is throttled for off-DOM videos and alone can stall to the cap, so it races
// against loadeddata/readyState>=2 plus a short paint grace.
const waitPresentableFrame = (video) =>
  new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    if (typeof video.requestVideoFrameCallback === "function") {
      try {
        video.requestVideoFrameCallback(() => finish());
      } catch (e) {
        // ignore - the races below still resolve
      }
    }
    const afterData = () => setTimeout(finish, LOADEDDATA_PAINT_GRACE_MS);
    if (video.readyState >= 2) afterData();
    else video.addEventListener("loadeddata", afterData, { once: true });
    setTimeout(finish, FRAME_WAIT_CAP_MS);
  });

const grabOneFrame = async (stream) => {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play();
  await waitPresentableFrame(video);
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;
  return drawScaled(video, width, height);
};

// Returns a JPEG data URL or null. NEVER throws and never leaves tracks running:
// cancel, permission-denied and no-transient-activation all mean "no shot".
export const captureViewport = async () => {
  let stream = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 1 },
      audio: false,
      // Cast past lib.dom: collapses Chrome's picker to one "share this tab" click.
      preferCurrentTab: true,
      selfBrowserSurface: "include",
    });
  } catch (err) {
    return null;
  }
  try {
    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), OVERALL_CAP_MS));
    return await Promise.race([grabOneFrame(stream), timeout]);
  } catch (err) {
    return null;
  } finally {
    try {
      stream.getTracks().forEach((track) => track.stop());
    } catch (e) {
      // already stopped
    }
  }
};

// One ingest path for paste / drop / upload. createImageBitmap decodes off the main
// thread; FileReader+Image is the fallback for formats it rejects.
export const imageFileToShot = async (file) => {
  try {
    const bitmap = await createImageBitmap(file);
    const dataUrl = drawScaled(bitmap, bitmap.width, bitmap.height);
    if (bitmap.close) bitmap.close();
    return dataUrl;
  } catch (err) {
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve(drawScaled(img, img.naturalWidth, img.naturalHeight));
        img.onerror = () => resolve(null);
        img.src = reader.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }
};

export const imageFilesFrom = (dataTransfer) =>
  Array.from(dataTransfer?.files || []).filter((file) => (file.type || "").startsWith("image/"));

export const dragCarriesFiles = (event) =>
  Array.from(event?.dataTransfer?.types || []).includes("Files");
